export type Currency = 'USD' | 'CAD' | 'GBP';

export interface ExchangeRates {
  USD: number;
  CAD: number;
  GBP: number;
}

export interface FirebaseExchangeRateResponse {
  rates: ExchangeRates;
  lastUpdated: string;
  source: string;
}

class CurrencyService {
  private readonly apiBaseUrl: string;
  private ratesCache: FirebaseExchangeRateResponse | null = null;
  private cacheExpiry: number = 0;
  private readonly cacheTimeout = 15 * 60 * 1000; // 15 minutes

  constructor() {
    this.apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5001';
  }

  /**
   * Fetch exchange rates from Firebase Functions
   */
  async getExchangeRates(): Promise<ExchangeRates> {
    // Return cached rates if still valid
    if (this.ratesCache && Date.now() < this.cacheExpiry) {
      return this.ratesCache.rates;
    }

    try {
      const response = await fetch(`${this.apiBaseUrl}/exchange-rates`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch exchange rates: ${response.status}`);
      }

      const data: FirebaseExchangeRateResponse = await response.json();
      
      // Validate the response structure
      if (!data.rates || typeof data.rates !== 'object') {
        throw new Error('Invalid exchange rates response format');
      }

      // Cache the response
      this.ratesCache = data;
      this.cacheExpiry = Date.now() + this.cacheTimeout;

      return data.rates;
    } catch (error) {
      console.error('Error fetching exchange rates:', error);
      
      // Return default rates if fetch fails
      return {
        USD: 1,
        CAD: 1.35,
        GBP: 0.79
      };
    }
  }

  /**
   * Convert price from USD to target currency
   */
  convertPrice(usdPrice: number, targetCurrency: Currency, exchangeRates: ExchangeRates): number {
    if (targetCurrency === 'USD') {
      return usdPrice;
    }
    
    const convertedPrice = usdPrice * exchangeRates[targetCurrency];
    // Apply ceiling rounding for non-USD currencies (same as customer app)
    return Math.ceil(convertedPrice);
  }

  /**
   * Format price with currency symbol
   */
  formatPrice(amount: number, currency: Currency): string {
    const symbols = {
      USD: '$',
      CAD: '$',
      GBP: '£'
    };

    return `${symbols[currency]}${amount.toFixed(2)}`;
  }

  /**
   * Get currency symbol
   */
  getCurrencySymbol(currency: Currency): string {
    const symbols = {
      USD: '$',
      CAD: '$', 
      GBP: '£'
    };

    return symbols[currency];
  }
}

// Export singleton instance
export const currencyService = new CurrencyService();